import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    Plus,
    Search,
    Filter,
    Pencil,
    Trash2,
    ArrowLeft,
    Dumbbell,
    X,
    CheckSquare,
    Square,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, EXERCISE_TYPES, CATEGORY_LABELS, EXERCISE_TYPE_LABELS } from "@/lib/workoutTaxonomy";
import { MAX_TYPE_META, MAX_TYPE_OPTIONS } from "@/lib/maxUnits";
import CreateExercisesDialog from "@/components/coach/CreateExercisesDialog";
import DuplicateExercisesDialog from "@/components/coach/DuplicateExercisesDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-black border border-zinc-800 px-4 py-2.5 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red";

export default function ExerciseLibrary() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [exercises, setExercises] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [busy, setBusy] = useState(true);
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [dupsOpen, setDupsOpen] = useState(false);
    const [dupCount, setDupCount] = useState(0);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && user.role !== "coach") navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const params = {};
            if (category) params.category = category;
            if (q.trim()) params.q = q.trim();
            const [ex, eq] = await Promise.all([
                axios.get(`${API}/coach/exercises`, { params, withCredentials: true }),
                axios.get(`${API}/equipment`, { withCredentials: true }),
            ]);
            setExercises(ex.data || []);
            setEquipment(eq.data || []);
        } catch (e) {
            toast.error("Couldn't load exercises", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    // Poll duplicate count separately so the header badge stays accurate
    // without forcing a full library reload every time.
    const refreshDupCount = async () => {
        try {
            const r = await axios.get(`${API}/coach/exercises/duplicates`, { withCredentials: true });
            setDupCount(r.data?.total_excess_rows || 0);
        } catch { /* non-fatal */ }
    };

    useEffect(() => {
        if (user?.role === "owner" || user?.role === "coach") {
            refresh();
            refreshDupCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, category]);

    const remove = async (ex) => {
        if (!window.confirm(`Delete "${ex.exercise_name}"?`)) return;
        try {
            await axios.delete(`${API}/coach/exercises/${ex.exercise_id}`, { withCredentials: true });
            toast.success("Deleted");
            refresh();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    const [selected, setSelected] = useState(new Set());
    const allSelected = exercises.length > 0 && exercises.every((e) => selected.has(e.exercise_id));
    const toggleSel = (id) => setSelected((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(exercises.map((e) => e.exercise_id)));
    const clearSel = () => setSelected(new Set());
    const bulkAction = async (action, extra = {}) => {
        if (selected.size === 0) return;
        try {
            const r = await axios.post(
                `${API}/coach/exercises/bulk-action`,
                { action, exercise_ids: [...selected], ...extra },
                { withCredentials: true },
            );
            toast.success(`${r.data?.affected || 0} updated`);
            clearSel();
            refresh();
        } catch (e) {
            toast.error("Bulk action failed", { description: e?.response?.data?.detail });
        }
    };
    const bulkDelete = async () => {
        if (!window.confirm(`Delete ${selected.size} exercise(s)? This soft-deletes them — historical data stays intact.`)) return;
        await bulkAction("delete");
    };
    const bulkChangeCategory = async () => {
        const cat = window.prompt(`Change category for ${selected.size} exercise(s) to one of: ${CATEGORIES.map((c) => c.value).join(", ")}`, "strength");
        if (!cat) return;
        await bulkAction("change_category", { category: cat });
    };
    const bulkMoveSubtab = async () => {
        const sub = window.prompt(`Move ${selected.size} exercise(s) to subtab (leave blank to clear)`, "");
        if (sub === null) return;
        await bulkAction("move_subtab", { subtab: sub });
    };
    const bulkSetVideo = async () => {
        const url = window.prompt(
            `Paste a YouTube or Vimeo URL to attach as the demo video on ${selected.size} exercise(s).\n\nLeave blank and hit OK to CLEAR the video on the selected rows.`,
            "",
        );
        if (url === null) return;  // cancelled
        try {
            const r = await axios.post(
                `${API}/coach/exercises/bulk-set-video`,
                { exercise_ids: [...selected], video_url: url.trim() || null },
                { withCredentials: true },
            );
            toast.success(
                url.trim()
                    ? `Video applied to ${r.data?.updated || 0} exercise${r.data?.updated === 1 ? "" : "s"}`
                    : `Video cleared on ${r.data?.updated || 0} exercise${r.data?.updated === 1 ? "" : "s"}`,
            );
            clearSel();
            refresh();
        } catch (e) {
            toast.error("Couldn't apply video", { description: e?.response?.data?.detail });
        }
    };

    if (loading || !user) return null;

    return (
        <div data-testid="exercise-library" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Link to="/coach" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </Link>
                    <div className="grid md:grid-cols-12 gap-6 items-end">
                        <div className="md:col-span-8">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" /> Library
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                                EXERCISE <span className="text-brand-red">LIBRARY.</span>
                            </h1>
                        </div>
                        <div className="md:col-span-4 md:text-right flex md:justify-end gap-2 flex-wrap">
                            <button
                                type="button"
                                data-testid="exercise-find-duplicates"
                                onClick={() => setDupsOpen(true)}
                                className={`btn-ghost-tactical inline-flex items-center gap-2 ${dupCount > 0 ? "border-amber-500 text-amber-300 hover:text-amber-100" : ""}`}
                                title="Scan the library for duplicate exercise names"
                            >
                                {dupCount > 0 ? `⚠ ${dupCount} duplicate${dupCount === 1 ? "" : "s"}` : "Find duplicates"}
                            </button>
                            <button
                                type="button"
                                data-testid="exercise-new"
                                onClick={() => setCreating(true)}
                                className="btn-tactical inline-flex"
                            >
                                <Plus className="w-4 h-4" /> Add exercise
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-5 border-b border-zinc-900 sticky top-16 md:top-20 bg-brand-obsidian/95 backdrop-blur z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row gap-4 md:items-center justify-between">
                    <form onSubmit={(e) => { e.preventDefault(); refresh(); }} className="flex items-center gap-2 flex-1 max-w-md">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search exercises…"
                                data-testid="exercise-search"
                                className={`pl-10 ${inputCls}`}
                            />
                        </div>
                        <button type="submit" className="btn-ghost-tactical">Go</button>
                    </form>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Filter className="w-3.5 h-3.5 text-zinc-300 mr-1" />
                        <button
                            type="button"
                            onClick={() => setCategory("")}
                            className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 ${category === "" ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200 hover:text-white"}`}
                        >All</button>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.value}
                                type="button"
                                data-testid={`exercise-filter-${c.value}`}
                                onClick={() => setCategory(c.value)}
                                className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 ${category === c.value ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200 hover:text-white"}`}
                            >{c.label}</button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    {busy ? (
                        <div className="py-16 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                        </div>
                    ) : exercises.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950 p-10 text-center">
                            <Dumbbell className="w-7 h-7 text-zinc-200 mx-auto mb-3" />
                            <div className="text-sm text-zinc-300">No exercises yet.</div>
                        </div>
                    ) : (
                        <>
                            {selected.size > 0 && (
                                <div data-testid="bulk-action-bar" className="mb-3 flex items-center justify-between gap-3 border border-brand-red/40 bg-brand-red/10 rounded-lg px-4 py-2.5">
                                    <div className="text-[11px] tracking-[0.2em] uppercase text-brand-red inline-flex items-center gap-2">
                                        <CheckSquare className="w-4 h-4" /> {selected.size} selected
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button type="button" data-testid="bulk-set-video" onClick={bulkSetVideo} className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-700 hover:border-brand-red hover:text-brand-red">Apply video</button>
                                        <button type="button" data-testid="bulk-move-subtab" onClick={bulkMoveSubtab} className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-700 hover:border-brand-red hover:text-brand-red">Move subtab</button>
                                        <button type="button" data-testid="bulk-change-category" onClick={bulkChangeCategory} className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-700 hover:border-brand-red hover:text-brand-red">Change category</button>
                                        <button type="button" data-testid="bulk-delete" onClick={bulkDelete} className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 bg-brand-red text-white">Delete</button>
                                        <button type="button" onClick={clearSel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            )}
                            <div className="border border-zinc-900">
                                <div className="px-5 py-2 border-b border-zinc-900 bg-zinc-900/30 flex items-center gap-3">
                                    <button type="button" data-testid="toggle-select-all" onClick={toggleAll} className="text-zinc-400 hover:text-brand-red">
                                        {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                    </button>
                                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Select all ({exercises.length})</span>
                                </div>
                                {exercises.map((ex) => {
                                    const isSel = selected.has(ex.exercise_id);
                                    return (
                                        <div
                                            key={ex.exercise_id}
                                            data-testid={`exercise-row-${ex.exercise_id}`}
                                            className={`px-5 py-3 flex items-center justify-between gap-4 border-b border-zinc-900 last:border-b-0 transition-colors ${isSel ? "bg-brand-red/5" : "bg-zinc-950 hover:bg-zinc-900/40"}`}
                                        >
                                            <button
                                                type="button"
                                                data-testid={`select-exercise-${ex.exercise_id}`}
                                                onClick={() => toggleSel(ex.exercise_id)}
                                                className="text-zinc-400 hover:text-brand-red shrink-0"
                                            >
                                                {isSel ? <CheckSquare className="w-4 h-4 text-brand-red" /> : <Square className="w-4 h-4" />}
                                            </button>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-white truncate">{ex.exercise_name}</div>
                                                <div className="text-[11px] text-zinc-300 truncate">
                                                    {CATEGORY_LABELS[ex.category]} · {EXERCISE_TYPE_LABELS[ex.exercise_type] || ex.exercise_type}
                                                    {ex.bar_equipment_id && ` · ${equipment.find((eq) => eq.equipment_id === ex.bar_equipment_id)?.name || "—"}`}
                                                    {ex.subtabs?.length > 0 && <> · <span className="text-brand-red">{ex.subtabs.join(" · ")}</span></>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                <button
                                                    type="button"
                                                    data-testid={`exercise-edit-${ex.exercise_id}`}
                                                    onClick={() => setEditing(ex)}
                                                    className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                                ><Pencil className="w-3.5 h-3.5" /> Edit</button>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(ex)}
                                                    className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                                ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {editing && (
                <ExerciseEditDialog
                    exercise={editing.exercise_id ? editing : null}
                    equipment={equipment}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}
            {creating && (
                <CreateExercisesDialog
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); refresh(); refreshDupCount(); }}
                />
            )}
            {dupsOpen && (
                <DuplicateExercisesDialog
                    onClose={() => setDupsOpen(false)}
                    onResolved={() => { refresh(); refreshDupCount(); }}
                />
            )}
        </div>
    );
}

function ExerciseEditDialog({ exercise, equipment, onClose, onSaved }) {
    const [name, setName] = useState(exercise?.exercise_name || "");
    const [category, setCategory] = useState(exercise?.category || "strength");
    const [exType, setExType] = useState(exercise?.exercise_type || "compound");
    const [equipmentId, setEquipmentId] = useState(exercise?.bar_equipment_id || "");
    const [description, setDescription] = useState(exercise?.description || "");
    // Spec-extension fields
    const [thumbnailUrl, setThumbnailUrl] = useState(exercise?.thumbnail_url || "");
    const [videoUrl, setVideoUrl] = useState(exercise?.video_url || "");
    const [instructions, setInstructions] = useState(exercise?.instructions || []);
    const [muscleGroups, setMuscleGroups] = useState((exercise?.muscle_groups || []).join(", "));
    const [secondaryMuscles, setSecondaryMuscles] = useState((exercise?.secondary_muscles || []).join(", "));
    const [equipmentList, setEquipmentList] = useState((exercise?.equipment || ["none"]).join(", "));
    const [difficulty, setDifficulty] = useState(exercise?.difficulty || "intermediate");
    const [movementCategory, setMovementCategory] = useState(exercise?.movement_category || "");
    const [rmValue, setRmValue] = useState(exercise?.rm_value || "");
    const [maxesConfig, setMaxesConfig] = useState(
        (exercise?.maxes_config && exercise.maxes_config.length > 0)
            ? exercise.maxes_config.map((m) => ({ type: m.type, label: m.label || "" }))
            : [{ type: "weight_lbs", label: "" }],
    );
    const [tips, setTips] = useState(exercise?.tips || []);
    const [commonMistakes, setCommonMistakes] = useState(exercise?.common_mistakes || []);
    const [variations, setVariations] = useState(exercise?.variations || []);
    const [saving, setSaving] = useState(false);

    const addMax = () => {
        if (maxesConfig.length >= 5) return;
        setMaxesConfig([...maxesConfig, { type: "weight_lbs", label: "" }]);
    };
    const removeMax = (idx) => {
        if (maxesConfig.length <= 1) return;
        setMaxesConfig(maxesConfig.filter((_, i) => i !== idx));
    };
    const setMaxType = (idx, type) => {
        setMaxesConfig(maxesConfig.map((m, i) => i === idx ? { ...m, type } : m));
    };
    const dupTypes = maxesConfig
        .map((m) => m.type)
        .filter((t, i, arr) => arr.indexOf(t) !== i);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = {
                exercise_name: name,
                category,
                exercise_type: exType,
                bar_equipment_id: equipmentId || null,
                description,
                thumbnail_url: thumbnailUrl || null,
                video_url: videoUrl || null,
                instructions: instructions.filter((s) => s && s.trim()),
                muscle_groups: muscleGroups.split(",").map((s) => s.trim()).filter(Boolean),
                secondary_muscles: secondaryMuscles.split(",").map((s) => s.trim()).filter(Boolean),
                equipment: equipmentList.split(",").map((s) => s.trim()).filter(Boolean) || ["none"],
                difficulty,
                movement_category: movementCategory || null,
                rm_value: rmValue || null,
                maxes_config: maxesConfig.map((m) => ({ type: m.type, label: m.label || null })),
                tips: tips.filter((s) => s && s.trim()),
                common_mistakes: commonMistakes.filter((s) => s && s.trim()),
                variations: variations.filter((s) => s && s.trim()),
            };
            if (exercise) {
                await axios.patch(`${API}/coach/exercises/${exercise.exercise_id}`, body, { withCredentials: true });
            } else {
                try {
                    await axios.post(`${API}/coach/exercises`, body, { withCredentials: true });
                } catch (err) {
                    if (err?.response?.status === 409) {
                        const detail = err.response.data?.detail;
                        const existing = (typeof detail === "object" ? detail?.existing : null);
                        const force = window.confirm(
                            `"${name}" already exists${existing ? ` (id: ${existing.exercise_id})` : ""}.\n\nOK = Create as v2 (force).\nCancel = Use existing instead.`,
                        );
                        if (!force) {
                            toast.info("Using existing exercise");
                            onClose();
                            return;
                        }
                        await axios.post(`${API}/coach/exercises/force`, body, { withCredentials: true });
                    } else {
                        throw err;
                    }
                }
            }
            toast.success(exercise ? "Updated" : "Created");
            onSaved();
        } catch (e2) {
            toast.error("Couldn't save", { description: typeof e2?.response?.data?.detail === "string" ? e2.response.data.detail : "Server error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto">
            <form
                onSubmit={submit}
                data-testid="exercise-edit-dialog"
                className="bg-zinc-950 border border-zinc-800 w-full md:max-w-2xl my-6 max-h-[90vh] flex flex-col"
            >
                <div className="h-1 bg-brand-red shrink-0" />
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900 shrink-0 bg-zinc-950">
                    <div className="font-display text-2xl tracking-wider text-white uppercase">
                        {exercise ? "Edit exercise" : "New exercise"}
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    <div>
                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">
                            Name <span className="text-brand-red">*</span>
                        </label>
                        <input
                            data-testid="exercise-name-input"
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Thumbnail URL</label>
                            <input value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)} placeholder="https://…" className={inputCls} />
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Video URL</label>
                            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/…" className={inputCls} />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Description</label>
                        <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
                    </div>
                    <ListEditor label="Instructions" value={instructions} onChange={setInstructions} placeholder="Step-by-step…" testIdPrefix="exercise-instruction" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">5S Category <span className="text-brand-red">*</span></label>
                            <select data-testid="exercise-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Movement category</label>
                            <select value={movementCategory} onChange={(e) => setMovementCategory(e.target.value)} className={inputCls}>
                                <option value="">—</option>
                                {["squat", "hinge", "push", "pull", "carry", "throw", "locomotion", "rotation"].map((m) => (
                                    <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Muscle groups (comma)</label>
                            <input value={muscleGroups} onChange={(e) => setMuscleGroups(e.target.value)} placeholder="quadriceps, glutes" className={inputCls} />
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Secondary (comma)</label>
                            <input value={secondaryMuscles} onChange={(e) => setSecondaryMuscles(e.target.value)} placeholder="core" className={inputCls} />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Equipment (comma)</label>
                        <input value={equipmentList} onChange={(e) => setEquipmentList(e.target.value)} placeholder="barbell, bench" className={inputCls} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Difficulty</label>
                            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inputCls}>
                                {["beginner", "intermediate", "advanced"].map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">RM value</label>
                            <select value={rmValue} onChange={(e) => setRmValue(e.target.value)} className={inputCls}>
                                <option value="">none</option>
                                {["1RM", "3RM", "5RM", "10RM"].map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Bar equipment</label>
                            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} className={inputCls}>
                                <option value="">None</option>
                                {equipment.map((eq) => (
                                    <option key={eq.equipment_id} value={eq.equipment_id}>
                                        {eq.name}{eq.weight ? ` (${eq.weight} lb)` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Maxes section — the heart of the form */}
                    <MaxesSection
                        rows={maxesConfig}
                        onAdd={addMax}
                        onRemove={removeMax}
                        onChangeType={setMaxType}
                        dupTypes={dupTypes}
                    />

                    {/* Type — keep legacy `exercise_type` for plate-calc compat */}
                    <div>
                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">Type (legacy)</label>
                        <select value={exType} onChange={(e) => setExType(e.target.value)} className={inputCls}>
                            {EXERCISE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>

                    <ListEditor label="Tips" value={tips} onChange={setTips} placeholder="One tip per line…" testIdPrefix="exercise-tip" />
                    <ListEditor label="Common mistakes" value={commonMistakes} onChange={setCommonMistakes} placeholder="What to watch for…" testIdPrefix="exercise-mistake" />
                    <ListEditor label="Variations" value={variations} onChange={setVariations} placeholder="Easier/harder progressions…" testIdPrefix="exercise-variation" />
                </div>
                <div className="px-6 py-4 border-t border-zinc-900 flex items-center justify-end gap-2 shrink-0 bg-zinc-950">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="exercise-save" className="btn-tactical disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {exercise ? "Save" : "Create"}
                    </button>
                </div>
            </form>
        </div>
    );
}


function MaxesSection({ rows, onAdd, onRemove, onChangeType, dupTypes }) {
    return (
        <div data-testid="maxes-section" className="border border-zinc-800 rounded-xl bg-zinc-900/30 p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Maxes ({rows.length}/5)</div>
                <button
                    type="button"
                    data-testid="add-max-row"
                    onClick={onAdd}
                    disabled={rows.length >= 5}
                    className="text-[11px] tracking-[0.15em] uppercase text-zinc-200 hover:text-brand-red disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                >
                    + Add max
                </button>
            </div>
            <div className="space-y-2">
                {rows.map((m, i) => (
                    <div key={i} data-testid={`max-row-${i}`} className="flex items-center gap-2">
                        <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 w-6">#{i + 1}</span>
                        <select
                            data-testid={`max-type-${i}`}
                            value={m.type}
                            onChange={(e) => onChangeType(i, e.target.value)}
                            className={`${inputCls} flex-1`}
                        >
                            {MAX_TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t}>{MAX_TYPE_META[t].dropdownLabel}</option>
                            ))}
                        </select>
                        {rows.length > 1 && (
                            <button
                                type="button"
                                data-testid={`remove-max-${i}`}
                                onClick={() => onRemove(i)}
                                className="text-zinc-400 hover:text-brand-red px-2 py-1"
                                title="Remove this max"
                            >×</button>
                        )}
                    </div>
                ))}
            </div>
            {dupTypes.length > 0 && (
                <div data-testid="maxes-dup-warning" className="mt-2 text-[11px] text-amber-300 bg-amber-900/10 border border-amber-600/40 rounded px-2 py-1.5 inline-flex items-center gap-1.5">
                    Same max type added twice — athletes will see two separate inputs.
                </div>
            )}
        </div>
    );
}


function ListEditor({ label, value, onChange, placeholder, testIdPrefix }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        onChange([...value, v]);
        setDraft("");
    };
    return (
        <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">{label}</label>
            <div className="flex gap-2">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder || ""}
                    data-testid={testIdPrefix ? `${testIdPrefix}-input` : undefined}
                    className={inputCls + " flex-1"}
                />
                <button type="button" onClick={add} className="btn-ghost-tactical text-xs">Add</button>
            </div>
            {value.length > 0 && (
                <ul className="mt-2 space-y-1">
                    {value.map((v, i) => (
                        <li key={i} className="text-sm text-zinc-300 flex items-center justify-between gap-2 border border-zinc-800 px-2.5 py-1.5 rounded">
                            <span className="break-words flex-1">{i + 1}. {v}</span>
                            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-brand-red">×</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
